/**
 * Create one voucher code -- phase 6b, Task 3.
 * POST /admin/discounts/promotions/:id/vouchers (MANUAL promotions only;
 * CodesTab never opens this for another kind).
 *
 * Body: `{ code, assignedUserId: uuid | null, redeemableByAssigneeOnly,
 * expiresAt: ISO | null }` -- blanks are sent as `null`, never `""`
 * (ADMIN_API.md nullable rule). A 409 means the code exists under ANY
 * manual promotion: the API copy is shown verbatim plus the UI hint that
 * codes are unique across all manual promotions. 422 field errors land
 * under their input (`code`, `assignedUserId`, `expiresAt`).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import type { CreateVoucherBody } from '../types';
import { localInputToIso } from './PromotionDialog';
import { CustomerPicker, type PickedCustomer } from './AssignCodeDialog';

/** schemas/vouchers.ts createVoucherBody.code */
export const CODE_RE = /^[A-Za-z0-9-]{3,40}$/;
export const UNIQUENESS_HINT = 'Codes are unique across all manual promotions, not just this one.';

export interface CreateCodeDialogProps {
  open: boolean;
  onClose: () => void;
  promotionId: string;
}

export const CreateCodeDialog: React.FC<CreateCodeDialogProps> = ({ open, onClose, promotionId }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [picked, setPicked] = useState<PickedCustomer | null>(null);
  const [assigneeOnly, setAssigneeOnly] = useState(true);
  const [expires, setExpires] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode('');
      setPicked(null);
      setAssigneeOnly(true);
      setExpires('');
      setErrors({});
      setTopError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: CreateVoucherBody) => discountsApi.createVoucher(promotionId, body),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: qk.all });
      notify(`Code ${saved.code} created`, { type: 'success' });
      onClose();
    },
    onError: (err) => {
      const f = toApiFailure(err);
      if (f.status === 409) {
        setErrors({ code: `${f.message} ${UNIQUENESS_HINT}` });
        setTopError(null);
        return;
      }
      // errors.ts never keys a whole-body 422 under '' -- that case arrives with
      // empty fieldErrors and lands in `message` below, like every other status.
      if (f.status === 422 && Object.keys(f.fieldErrors).length) {
        setErrors(f.fieldErrors);
        setTopError(null);
        return;
      }
      setErrors({});
      setTopError(f.message);
    },
  });

  const submit = () => {
    const trimmed = code.trim();
    if (!CODE_RE.test(trimmed)) {
      setErrors({ code: '3–40 letters, digits or dashes.' });
      return;
    }
    setErrors({});
    setTopError(null);
    create.mutate({
      code: trimmed,
      assignedUserId: picked ? picked.id : null,
      redeemableByAssigneeOnly: picked ? assigneeOnly : false,
      expiresAt: localInputToIso(expires),
    });
  };

  const busy = create.isPending;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>New code</DialogTitle>
      <DialogContent>
        {topError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {topError}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            id="voucher-code"
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            required
            disabled={busy}
            error={Boolean(errors.code)}
            helperText={errors.code ?? 'What the customer types at checkout: 3–40 letters, digits or dashes.'}
            slotProps={{ htmlInput: { maxLength: 40, style: { fontFamily: 'monospace' }, autoCapitalize: 'characters' } }}
          />
          <CustomerPicker
            label="Assign to (optional)"
            value={picked}
            onChange={setPicked}
            error={errors.assignedUserId ?? null}
            disabled={busy}
          />
          {picked && (
            <FormControlLabel
              control={<Checkbox checked={assigneeOnly} onChange={(e) => setAssigneeOnly(e.target.checked)} disabled={busy} />}
              label="Only the assignee can redeem this code"
            />
          )}
          <TextField
            id="voucher-expiresAt"
            label="Expires"
            type="datetime-local"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            disabled={busy}
            error={Boolean(errors.expiresAt)}
            helperText={errors.expiresAt ?? 'Leave blank for no expiry.'}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || !code.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCodeDialog;
